import React, { useState } from 'react';
import { TextField, Typography, Alert, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import logoImg from '../../shared/svg/AppLogo/management.png';
import { authService } from '../../services/authService';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
    ResetPasswordContainer,
    ResetPasswordWrapper,
    LogoContainer,
    FormTitle,
    FormDescription,
    ButtonsContainer,
    BackButton,
    SubmitButton
} from './Styled';

type PasswordFormData = {
    new_password: string;
    confirm_password: string;
};

const ResetPassword = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const [formData, setFormData] = useState<PasswordFormData>({
        new_password: '',
        confirm_password: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        if (formData.new_password !== formData.confirm_password) {
            setError('Mật khẩu xác nhận không trùng khớp.');
            setIsLoading(false);
            return;
        }

        try {
            if (!token) {
                throw new Error('Token không hợp lệ');
            }
            
            await authService.resetPassword({ 
                token: token,
                new_password: formData.new_password, 
                confirm_password: formData.confirm_password
            });
            setSuccess(true);
            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err: any) {
            if (err.response?.data?.token) {
                setError(err.response.data.token);
            } else if (err.response?.data?.new_password) {
                setError(err.response.data.new_password);
            } else if (err.response?.data?.confirm_password) {
                setError(err.response.data.confirm_password);
            } else if (err.response?.data?.error) {
                setError(err.response.data.error);
            } else {
                setError('Đã xảy ra lỗi. Vui lòng thử lại sau.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (!token) {
        return (
            <ResetPasswordContainer maxWidth={false}>
                <ResetPasswordWrapper elevation={3}>
                    <LogoContainer>
                        <img src={logoImg} alt="Logo" />
                        <Typography variant="h5" ml={1} fontWeight="700" fontSize="32px">PMS</Typography>
                    </LogoContainer>
                    <FormTitle variant="h6">
                        Liên kết không hợp lệ
                    </FormTitle>
                    
                    <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
                        Liên kết không hợp lệ. Vui lòng kiểm tra lại email của bạn.
                    </Alert>
                    <ButtonsContainer>
                        <BackButton
                            variant="contained"
                            onClick={handleBackToLogin}
                        >
                            Quay lại đăng nhập
                        </BackButton>
                    </ButtonsContainer>
                </ResetPasswordWrapper>
            </ResetPasswordContainer>
        );
    }

    return (
        <ResetPasswordContainer maxWidth={false}>
            <ResetPasswordWrapper elevation={3}>
                <LogoContainer>
                    <img src={logoImg} alt="Logo" />
                    <Typography variant="h5" ml={1} fontWeight="700" fontSize="32px">PMS</Typography>
                </LogoContainer>

                {success ? (
                    <>
                        <FormTitle variant="h6">
                            Đặt lại mật khẩu thành công
                        </FormTitle>
                        
                        <Alert severity="success" sx={{ mb: 3, borderRadius: '8px' }}>
                            Mật khẩu đã được đặt lại thành công! Bạn sẽ được chuyển hướng đến trang đăng nhập.
                        </Alert>
                        <ButtonsContainer>
                            <BackButton
                                variant="contained"
                                onClick={handleBackToLogin}
                            >
                                Đăng nhập ngay
                            </BackButton>
                        </ButtonsContainer>
                    </>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <FormTitle variant="h6">
                            Đặt lại mật khẩu
                        </FormTitle>
                        
                        {error && (
                            <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
                                {error}
                            </Alert>
                        )}

                        <FormDescription variant="body2">
                            Vui lòng nhập mật khẩu mới của bạn.
                        </FormDescription>

                        <TextField
                            fullWidth
                            label="Mật khẩu mới"
                            name="new_password"
                            type={showPassword ? 'text' : 'password'}
                            variant="outlined"
                            value={formData.new_password}
                            onChange={handleInputChange}
                            required
                            disabled={isLoading}
                            sx={{ 
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px'
                                },
                                '& .MuiFormLabel-asterisk': {
                                    color: '#d32f2f',
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={togglePasswordVisibility}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Xác nhận mật khẩu"
                            name="confirm_password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            variant="outlined"
                            value={formData.confirm_password}
                            onChange={handleInputChange}
                            required
                            disabled={isLoading}
                            sx={{ 
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px'
                                },
                                '& .MuiFormLabel-asterisk': {
                                    color: '#d32f2f',
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={toggleConfirmPasswordVisibility}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <ButtonsContainer>
                            <BackButton
                                variant="contained"
                                onClick={handleBackToLogin}
                                disabled={isLoading}
                            >
                                Trở lại
                            </BackButton>
                            <SubmitButton
                                type="submit"
                                variant="contained"
                                disabled={isLoading}
                            >
                                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Đặt lại mật khẩu'}
                            </SubmitButton>
                        </ButtonsContainer>
                    </form>
                )}
            </ResetPasswordWrapper>
        </ResetPasswordContainer>
    );
};

export default ResetPassword; 